import { ARMOR_BOOST_MAX, ARMOR_BOOST_PER_SHIP, ARMOR_TYPE_REDUCTION, getShipDefinition, BATTLE_HIT_STEP_MULTIPLIERS, BATTLE_MAX_HITS_PER_GROUP, BATTLE_PENALTY_MULTIPLIER, BATTLE_POINTS_FACTOR, BATTLE_POINTS_NO_LOSS_FACTOR, BATTLE_PRIORITY_BONUS_MULTIPLIER, BONUS_LIFE_MAX, BONUS_LIFE_PER_SHIP, CRITICAL_DAMAGE_MAX_MULTIPLIER, CRITICAL_DAMAGE_MIN_MULTIPLIER, DEMOLITION_CHANCE_TABLE, DEMOLITION_POINTS_PER_SHIP, DEVASTATE_CHANCE_PER_SHIP, DEVASTATE_MAX_CHANCE, FREEZING_CHANCE_PER_SHIP, FREEZING_MAX_CHANCE, IGNORE_ARMOR_CHANCE_PER_SHIP, IGNORE_ARMOR_MAX_CHANCE, MEGA_POWER_ATTACK_BONUS_PER_SHIP, MEGA_POWER_MAX_ACTIVATION_CHANCE, MEGA_POWER_MAX_ATTACK_BONUS, MEGA_POWER_ACTIVATION_PER_SHIP, PLAYER_POINTS_DIVISOR, REVIVAL_ACTIVATION_PER_SHIP, REVIVAL_MAX_ACTIVATION_CHANCE, REVIVAL_MAX_REVIVE_PERCENT, REVIVAL_POWER_PER_SHIP, SCRAP_MULTIPLIER_MAX_STEP, SCRAP_MULTIPLIER_MIN_STEP, SCRAP_MULTIPLIER_STEP_PERCENT, SHIELD_TAUNT_SHIP_ID, SHIP_DEFINITIONS, } from "@kaeliux/shared";
import { getScienceBonus } from "./sciences.js";
import { calculateShipBuildCost, getUpgradedShipStats } from "./ships.js";
/**
 * Pure battle resolution (#18) — the simultaneous model. No side effects, no
 * `Math.random`/`Date.now` — every stochastic event (crits, active-skill
 * activations, scrap yield) is decided by an entry from the caller-supplied
 * `rolls: number[]` (each 0..1), consumed via an internal cursor in a fixed,
 * documented order — the same "roll outside, pass in" pattern
 * `fleetResolution.ts` already uses for `asteroidRoll`. Same input
 * (including the same rolls) always produces the same output.
 *
 * DEV-ONLY: `fleetResolution.ts` resolves real attack/pirate combat with the
 * sequential ("back-and-forth") model in `battleSequential.ts` instead. This
 * simultaneous model is kept as a side-by-side comparison on the Battle
 * Simulator page (`apps/client`) and in `battleComparison.test.ts`.
 *
 * Roll order (simultaneous model): per round, Mega Power activation (attacker,
 * defender; only with a live provider) -> freezing (attacker->defender,
 * defender->attacker) -> per hit of each firing group, in firing order: one
 * critical roll, then one Ignore Armor roll (only when the round's chance is
 * above 0, the firing ship id is not an Ignore Armor provider and the group is
 * not frozen) -> revival (attacker, defender). One scrap roll closes the battle.
 * Passive bonuses (Bonus Life, Armor Boost) and the Devastate chance are
 * recalculated at the start of every round from the providers still alive, and
 * the Bonus Life layer is removed when the battle ends.
 *
 * Rule reference: `docs/game-details/battle_system.md` and
 * `docs/game-details/ships.md` "Ship Abilities", as revised by the
 * battle-system implementation plan (round counts, 3-hit/100-66-33% decay,
 * 1.5x priority bonus, shield-ship taunt, and the id3/id5 skill swap in
 * `constants/ships.ts`).
 */
// ---------------------------------------------------------------------------
// Roll cursor — a tiny stateful helper local to one `resolveBattle` call.
// ---------------------------------------------------------------------------
export class RollCursor {
    constructor(rolls) {
        this.rolls = rolls;
        this.index = 0;
    }
    /** Next 0..1 roll, or `1` (never triggers a chance check) once exhausted. */
    next() {
        const roll = this.rolls[this.index];
        this.index += 1;
        return roll ?? 1;
    }
}
// ---------------------------------------------------------------------------
// Unit-group construction
// ---------------------------------------------------------------------------
function weaponAttackMultiplier(def, bonuses) {
    switch (def.weaponType) {
        case "laser": return bonuses.laser.laserAttackMultiplier;
        case "ion": return bonuses.ion.ionAttackMultiplier;
        case "plasma": return bonuses.plasma.plasmaAttackMultiplier;
    }
}
function armorScienceBonus(def, bonuses) {
    switch (def.armorType) {
        case "light": return bonuses.polymerShields.armorBonus;
        case "medium": return bonuses.carbonFiberShields.armorBonus;
        case "heavy": return bonuses.heavyMetalShields.armorBonus;
    }
}
const SKILL_BITS = {
    ignoreArmor: 1 << 0,
    devastate: 1 << 1,
    bonusLife: 1 << 2,
    armorBoost: 1 << 3,
    revival: 1 << 4,
    megaPower: 1 << 5,
    freezing: 1 << 6,
    artillery: 1 << 7,
    destroyBuildings: 1 << 8,
    destroyPlanets: 1 << 9,
};
/** What each ship provides, as skill bits, read from the ship definitions once: `SKILLS_PROVIDED[race][shipId]`. */
const SKILLS_PROVIDED = Object.fromEntries(Object.keys(SHIP_DEFINITIONS).map((race) => {
    const masks = [];
    for (const def of Object.values(SHIP_DEFINITIONS[race])) {
        masks[def.id] = (def.skills ?? []).reduce((mask, skill) => mask | SKILL_BITS[skill], 0);
    }
    return [race, masks];
}));
const NO_PROVIDERS = Object.freeze([]);
function unknownShipSkills(group) {
    getShipDefinition(group.race, group.shipId);
    return 0;
}
/** Every LIVE group, of any race, whose OWN race's ship definition carries `skill` — so a mixed-race side never credits a skill to a ship that lacks it. Throws for a live group of an unknown race or ship. */
export function liveProviders(groups, skill) {
    const bit = SKILL_BITS[skill];
    let providers;
    for (const g of groups) {
        if (g.count <= 0)
            continue;
        const mask = SKILLS_PROVIDED[g.race]?.[g.shipId] ?? unknownShipSkills(g);
        if ((mask & bit) === 0)
            continue;
        (providers ?? (providers = [])).push(g);
    }
    return providers ?? NO_PROVIDERS;
}
/** Sum of `perShip(provider.race) × provider.count` over every provider, capped once at `max` AFTER summing (cross-race strengths add up before the cap). */
function summedStrength(providers, perShip, max) {
    let total = 0;
    for (const p of providers)
        total += perShip(p.race) * p.count;
    return Math.min(max, total);
}
/** Ship ids of the providers — every group of one of these ship ids, in any race, misses the bonus the providers grant. */
function providerShipIds(providers) {
    const ids = [];
    for (const p of providers) {
        if (!ids.includes(p.shipId))
            ids.push(p.shipId);
    }
    return ids;
}
export function isGroupKind(group, kind) {
    return kind !== undefined && group.shipId === kind.shipId && group.race === kind.race;
}
/**
 * One side's live unit groups with their BASE stats: base × ship-upgrade level
 * (`getUpgradedShipStats`) × science bonuses (`getScienceBonus`) ×
 * `admiralBonuses` (no-op today). Passive fleet-wide skills (Bonus Life, Armor
 * Boost) are NOT included — they are recalculated at the start of every round
 * from the providers still alive (`applyRoundStartPassives`), and the Bonus
 * Life layer is removed again when the battle ends (`removeBonusLifeLayers`).
 * Per-round active skills (Ignore Armor, Devastate, Mega Power, Freezing,
 * Revival) are likewise resolved dynamically during combat.
 */
export function buildUnitGroups(side) {
    const scienceBonus = getScienceBonus(side.sciences);
    const admiral = side.admiralBonuses ?? {};
    const groups = [];
    for (const [key, count] of Object.entries(side.ships)) {
        if (!count)
            continue;
        const shipId = Number(key);
        const def = getShipDefinition(side.race, shipId);
        const upgradeLevel = side.shipUpgrades?.[shipId] ?? 0;
        const upgraded = getUpgradedShipStats(def, upgradeLevel);
        const attackMultiplier = weaponAttackMultiplier(def, scienceBonus) * scienceBonus.offensiveTactics.shipAttackMultiplier;
        const lifeMultiplier = scienceBonus.defenceSystems.shipLifeMultiplier * scienceBonus.defensiveTactics.shipLifeMultiplier;
        const armor = ARMOR_TYPE_REDUCTION[def.armorType] + armorScienceBonus(def, scienceBonus);
        const stats = {
            attack: upgraded.attack * attackMultiplier * (1 + (admiral.shipAttackMultiplier ?? 0)),
            life: upgraded.life * lifeMultiplier * (1 + (admiral.shipLifeMultiplier ?? 0)),
            armor: armor + (admiral.armorBonus ?? 0),
        };
        groups.push({ shipId: def.id, race: side.race, category: def.category, count, perUnit: stats });
    }
    return groups;
}
/**
 * The passive percentages `groups` grant right now: summed across EVERY live
 * providing group of every race — each at its own race's per-ship rate — and
 * capped once on the total. Groups of a providing ship id (in any race) never
 * receive the bonus themselves. Pass a round snapshot's groups to read that
 * round's percentages.
 */
export function sidePassiveBonusPercents(groups) {
    return {
        bonusLife: summedStrength(liveProviders(groups, "bonusLife"), (race) => BONUS_LIFE_PER_SHIP[race], BONUS_LIFE_MAX),
        armorBoost: summedStrength(liveProviders(groups, "armorBoost"), (race) => ARMOR_BOOST_PER_SHIP[race], ARMOR_BOOST_MAX),
    };
}
/** Records each group's CURRENT `perUnit` life and armor as its base. Call on freshly built groups, before any passive is applied. */
export function initPassiveLayers(groups) {
    return new Map(groups.map((g) => [g, { life: g.perUnit.life, armor: g.perUnit.armor, lifePercent: 0 }]));
}
/**
 * Sets `group`'s Bonus Life layer to `percent` (a fraction). The layer is
 * replaced, never stacked: `perUnit.life = baseLife × (1 + percent)`, and the
 * life pool moves by `(percent − previous) × baseLife × CURRENT count`. The
 * count itself is not changed here (the next hit recounts it with the survivor
 * formula) — except that a pool taken to 0 or below destroys the group's ships.
 */
export function applyBonusLifeLayer(group, percent, layers, lifePool) {
    const layer = layers.get(group);
    if (!layer)
        return;
    const previous = layer.lifePercent;
    layer.lifePercent = percent;
    group.perUnit.life = layer.life * (1 + percent);
    if (group.count <= 0)
        return;
    const before = lifePool.get(group) ?? layer.life * (1 + previous) * group.count;
    const after = before + (percent - previous) * layer.life * group.count;
    if (after <= 0) {
        lifePool.set(group, 0);
        group.count = 0;
        return;
    }
    lifePool.set(group, after);
}
/**
 * Start-of-round step for one side: recomputes Bonus Life and Armor Boost from
 * the providers alive right now and applies them to every group that is not of
 * a providing ship id. Bonus Life goes first (it can destroy a last ship), so
 * Armor Boost is summed from whoever is still alive. Run it BEFORE the round's
 * snapshot is cloned, so the snapshot shows this round's stats. Returns the
 * percentages applied.
 */
export function applyRoundStartPassives(groups, layers, lifePool) {
    const lifeProviders = liveProviders(groups, "bonusLife");
    const lifePercent = summedStrength(lifeProviders, (race) => BONUS_LIFE_PER_SHIP[race], BONUS_LIFE_MAX);
    const lifeExcluded = providerShipIds(lifeProviders);
    for (const g of groups) {
        applyBonusLifeLayer(g, lifeExcluded.includes(g.shipId) ? 0 : lifePercent, layers, lifePool);
    }
    const armorProviders = liveProviders(groups, "armorBoost");
    const armorPercent = summedStrength(armorProviders, (race) => ARMOR_BOOST_PER_SHIP[race], ARMOR_BOOST_MAX);
    const armorExcluded = providerShipIds(armorProviders);
    for (const g of groups) {
        const layer = layers.get(g);
        if (layer)
            g.perUnit.armor = layer.armor + (armorExcluded.includes(g.shipId) ? 0 : armorPercent);
    }
    return { bonusLife: lifePercent, armorBoost: armorPercent };
}
/**
 * Battle end: removes every group's Bonus Life layer from its pool
 * (`pool −= layer% × baseLife × count`). A group whose pool falls to 0 or below
 * is destroyed — those ships count as losses; otherwise the count is unchanged.
 * Call before survivors, losses and points are computed.
 */
export function removeBonusLifeLayers(groups, layers, lifePool) {
    for (const g of groups)
        applyBonusLifeLayer(g, 0, layers, lifePool);
}
// ---------------------------------------------------------------------------
// Targeting
// ---------------------------------------------------------------------------
const COMBAT_ID_ORDER = [9, 8, 7, 6, 5, 4, 3];
/**
 * Resolve `firingGroup`'s target among `enemyGroups` (already filtered to
 * this side by the caller). Order: (1) shield-ship taunt — id5 is targeted
 * first by everyone while any are alive, overriding the firing ship's own
 * priority target; (2) the firing ship's own `priorityTargets` row; (3)
 * generic combat-ship ID order; (4) civilian (ascending ID); (5) service.
 * Commander ships don't exist yet (admiral/commander system, deferred).
 */
export function selectTarget(firingDef, enemyGroups) {
    const alive = (id) => enemyGroups.find((g) => g.shipId === id && g.count > 0);
    const taunt = alive(SHIELD_TAUNT_SHIP_ID);
    if (taunt)
        return taunt;
    for (const id of firingDef.priorityTargets ?? []) {
        const target = alive(id);
        if (target)
            return target;
    }
    for (const id of COMBAT_ID_ORDER) {
        const target = alive(id);
        if (target)
            return target;
    }
    const civilian = enemyGroups
        .filter((g) => g.category === "civilian" && g.count > 0)
        .sort((a, b) => a.shipId - b.shipId)[0];
    if (civilian)
        return civilian;
    const service = enemyGroups.find((g) => g.category === "service" && g.count > 0);
    if (service)
        return service;
    return undefined;
}
// ---------------------------------------------------------------------------
// Per-hit damage
// ---------------------------------------------------------------------------
function targetMultiplier(firingDef, targetShipId) {
    if (firingDef.bonusTargets?.includes(targetShipId))
        return BATTLE_PRIORITY_BONUS_MULTIPLIER;
    if (firingDef.penaltyTargets?.includes(targetShipId))
        return BATTLE_PENALTY_MULTIPLIER;
    return 1;
}
/** Full per-hit pipeline. `isFrozen` short-circuits to zero damage before anything else. */
export function computeHitDamage(firingGroup, firingDef, target, hitStep, options) {
    if (options.isFrozen) {
        return { rawDamage: 0, finalDamage: 0, wasCritical: false, wasArmorIgnored: false };
    }
    const base = firingGroup.perUnit.attack * firingGroup.count;
    const stepMultiplier = BATTLE_HIT_STEP_MULTIPLIERS[hitStep];
    const tMultiplier = targetMultiplier(firingDef, target.shipId);
    const megaPower = 1 + options.megaPowerBonus;
    let raw = base * stepMultiplier * tMultiplier * megaPower;
    const wasCritical = options.criticalRoll < options.criticalChance;
    if (wasCritical) {
        const critRange = CRITICAL_DAMAGE_MAX_MULTIPLIER - CRITICAL_DAMAGE_MIN_MULTIPLIER;
        // The roll that decided "is this a crit" also picks WHERE in the
        // 2x-2.5x band it lands — reusing it (instead of consuming a second
        // roll) keeps a hit's outcome fully determined by one entry.
        const critMultiplier = CRITICAL_DAMAGE_MIN_MULTIPLIER + (options.criticalRoll / options.criticalChance) * critRange;
        raw *= critMultiplier;
    }
    const armor = options.ignoreArmorActive ? 0 : target.perUnit.armor;
    const finalDamage = raw * (1 - armor);
    return { rawDamage: raw, finalDamage, wasCritical, wasArmorIgnored: options.ignoreArmorActive };
}
export function initLifePool(groups) {
    return new Map(groups.map((g) => [g, g.perUnit.life * g.count]));
}
/** Apply `damage` to `target`'s life pool, updating both the pool and `target.count` (whole units remaining). */
export function applyDamage(target, damage, lifePool) {
    const before = lifePool.get(target) ?? target.perUnit.life * target.count;
    if (damage <= 0 || target.perUnit.life <= 0)
        return { destroyed: 0, remainingCount: target.count };
    if (damage >= before) {
        lifePool.set(target, 0);
        const destroyed = target.count;
        target.count = 0;
        return { destroyed, remainingCount: 0 };
    }
    const after = before - damage;
    lifePool.set(target, after);
    const remainingCount = Math.ceil(after / target.perUnit.life);
    const destroyed = target.count - remainingCount;
    target.count = remainingCount;
    return { destroyed, remainingCount };
}
/**
 * Start-of-round skill state for one side. Ignore Armor consumes no roll here
 * (it rolls per hit — see `rollIgnoreArmor`). Mega Power rolls once, only when
 * the side has a live provider: activation chance and attack bonus both sum
 * over every provider and are capped on the total.
 */
export function resolveStartOfRoundSkills(groups, rolls) {
    const ignoreArmorProviders = liveProviders(groups, "ignoreArmor");
    const ignoreArmorChance = summedStrength(ignoreArmorProviders, (race) => IGNORE_ARMOR_CHANCE_PER_SHIP[race], IGNORE_ARMOR_MAX_CHANCE);
    let megaPowerBonus = 0;
    let megaPowerSourceRace;
    const megaPowerProviders = liveProviders(groups, "megaPower");
    if (megaPowerProviders.length > 0) {
        const chance = summedStrength(megaPowerProviders, () => MEGA_POWER_ACTIVATION_PER_SHIP, MEGA_POWER_MAX_ACTIVATION_CHANCE);
        if (rolls.next() < chance) {
            megaPowerBonus = summedStrength(megaPowerProviders, () => MEGA_POWER_ATTACK_BONUS_PER_SHIP, MEGA_POWER_MAX_ATTACK_BONUS);
            megaPowerSourceRace = megaPowerProviders[0].race;
        }
    }
    return {
        ignoreArmorChance,
        ignoreArmorExcludedShipIds: providerShipIds(ignoreArmorProviders),
        megaPowerBonus,
        megaPowerExcludedShipIds: providerShipIds(megaPowerProviders),
        megaPowerSourceRace,
    };
}
/**
 * One Ignore Armor roll for one hit. Consumed only when the round's chance is
 * above 0, the firing ship id is not an Ignore Armor provider, and the hit is
 * not frozen — so a battle without Ignore Armor consumes no extra rolls.
 */
export function rollIgnoreArmor(skills, firingShipId, isFrozen, rolls) {
    if (isFrozen || skills.ignoreArmorChance <= 0 || skills.ignoreArmorExcludedShipIds.includes(firingShipId))
        return false;
    return rolls.next() < skills.ignoreArmorChance;
}
/**
 * Every per-hit roll, in order: the critical roll (always consumed, even for a
 * frozen hit), then the Ignore Armor roll (see `rollIgnoreArmor`). Both
 * battle models call this once per combat-log entry.
 */
export function rollHit(skills, firingShipId, isFrozen, rolls) {
    const criticalRoll = rolls.next();
    const ignoreArmorActive = rollIgnoreArmor(skills, firingShipId, isFrozen, rolls);
    return { criticalRoll, ignoreArmorActive };
}
/** Mega Power attack bonus the firing ship id receives this round (providers never get it). */
export function megaPowerBonusFor(skills, firingShipId) {
    return skills.megaPowerExcludedShipIds.includes(firingShipId) ? 0 : skills.megaPowerBonus;
}
/**
 * Freezing: rolled once per round. The activation chance sums over every live
 * Freezing provider (capped on the total). On activation it freezes a
 * UNIFORMLY-RANDOM living enemy group (picked from a second roll) — freezing is
 * indiscriminate and does NOT follow the Medusa's targeting priority. Consumes
 * the pick roll only when it activates. Returns the frozen group's ship id and
 * own race, and the first provider's race as `sourceRace`, or undefined if it
 * didn't activate or no enemy group is alive.
 */
export function resolveFreezing(freezerGroups, enemyGroups, rolls) {
    const providers = liveProviders(freezerGroups, "freezing");
    if (providers.length === 0)
        return undefined;
    const chance = summedStrength(providers, () => FREEZING_CHANCE_PER_SHIP, FREEZING_MAX_CHANCE);
    if (rolls.next() >= chance)
        return undefined;
    const livingTargets = enemyGroups.filter((g) => g.count > 0);
    if (livingTargets.length === 0)
        return undefined;
    const pickIndex = Math.min(livingTargets.length - 1, Math.floor(rolls.next() * livingTargets.length));
    const picked = livingTargets[pickIndex];
    return picked ? { frozen: { shipId: picked.shipId, race: picked.race }, sourceRace: providers[0].race } : undefined;
}
// ---------------------------------------------------------------------------
// One side firing its whole force for the round
// ---------------------------------------------------------------------------
/**
 * Rolls per hit, in order: critical roll, then the Ignore Armor roll (only
 * when `rollIgnoreArmor` consumes one). A frozen hit still consumes its
 * critical roll but never an Ignore Armor roll.
 */
export function fireSide(firingGroups, firingSideLabel, enemyGroups, round, skills, frozen, critChance, rolls, lifePool) {
    const entries = [];
    for (const firingGroup of firingGroups) {
        if (firingGroup.count <= 0)
            continue;
        const firingDef = getShipDefinition(firingGroup.race, firingGroup.shipId);
        const isFrozen = isGroupKind(firingGroup, frozen);
        const megaPowerBonus = megaPowerBonusFor(skills, firingGroup.shipId);
        const groupCritChance = critChanceForShip(critChance, firingGroup.shipId);
        for (let hitStep = 0; hitStep < BATTLE_MAX_HITS_PER_GROUP; hitStep++) {
            const target = selectTarget(firingDef, enemyGroups);
            if (!target)
                break;
            const targetLifeBefore = lifePool.get(target) ?? target.perUnit.life * target.count;
            const { criticalRoll, ignoreArmorActive } = rollHit(skills, firingGroup.shipId, isFrozen, rolls);
            const hit = computeHitDamage(firingGroup, firingDef, target, hitStep, {
                megaPowerBonus,
                isFrozen,
                ignoreArmorActive,
                criticalChance: groupCritChance,
                criticalRoll,
            });
            const { destroyed, remainingCount } = applyDamage(target, hit.finalDamage, lifePool);
            entries.push({
                round,
                firingSide: firingSideLabel,
                firingShipId: firingGroup.shipId,
                firingRace: firingGroup.race,
                firingCount: firingGroup.count,
                targetShipId: target.shipId,
                targetRace: target.race,
                hitStep: hitStep,
                rawDamage: hit.rawDamage,
                finalDamage: hit.finalDamage,
                unitsDestroyed: destroyed,
                wasCritical: hit.wasCritical,
                wasArmorIgnored: hit.wasArmorIgnored,
                wasFrozen: isFrozen,
                targetLifeBefore,
                targetArmorPercent: hit.wasArmorIgnored ? 0 : target.perUnit.armor,
            });
            if (isFrozen)
                break; // a frozen group's whole hit sequence deals no damage
            if (remainingCount > 0)
                break; // target survived — this group stops attacking for the round
        }
    }
    return entries;
}
// ---------------------------------------------------------------------------
// Revival (end of round)
// ---------------------------------------------------------------------------
/** Whole units each live group lost DURING the round (start count − current count), keyed by the group itself so pooled fleets sharing a ship id never collide. */
export function roundLossCounts(groups, startCounts) {
    const losses = new Map();
    for (const g of groups) {
        const before = startCounts.get(g) ?? g.count;
        const lost = Math.max(0, before - g.count);
        if (lost > 0)
            losses.set(g, lost);
    }
    return losses;
}
/**
 * Revival (Phoenix): on activation, revives `min(REVIVAL_MAX_REVIVE_PERCENT,
 * Σ REVIVAL_POWER_PER_SHIP × Phoenix ships)` of the ships this side lost THIS ROUND —
 * never a growing cumulative pool, so a round can never revive more than it just
 * lost (ships.md "up to 40%"). Activation chance and power sum over every live
 * provider and are capped on the total. Each group is revived from its OWN
 * losses. Groups of a providing ship id (any race) are never revived (ships.md:
 * abilities "apply to all allied ships except the ones providing the ability
 * themselves"). Revived ships rejoin at the group's current per-ship life
 * (Bonus Life layer included) for the next round / final fleet.
 */
export function resolveRevival(groups, side, lossesThisRound, rolls, lifePool) {
    const events = [];
    const providers = liveProviders(groups, "revival");
    if (providers.length === 0)
        return events;
    const activation = summedStrength(providers, () => REVIVAL_ACTIVATION_PER_SHIP, REVIVAL_MAX_ACTIVATION_CHANCE);
    if (rolls.next() >= activation)
        return events;
    const revivePercent = summedStrength(providers, () => REVIVAL_POWER_PER_SHIP, REVIVAL_MAX_REVIVE_PERCENT);
    const excluded = providerShipIds(providers);
    const sourceRace = providers[0].race;
    for (const g of groups) {
        if (excluded.includes(g.shipId))
            continue;
        const revivedCount = Math.floor((lossesThisRound.get(g) ?? 0) * revivePercent);
        if (revivedCount <= 0)
            continue;
        g.count += revivedCount;
        lifePool.set(g, (lifePool.get(g) ?? 0) + revivedCount * g.perUnit.life);
        events.push({
            type: "revival",
            side,
            sourceRace,
            revivedShipId: g.shipId,
            revivedRace: g.race,
            revivedCount,
        });
    }
    return events;
}
/** The round's Mega Power and Freezing events, each stamped with the race of the group whose skill fired and (for Freezing) of the group it froze. */
export function buildStartOfRoundEvents(attackerSkills, defenderSkills, frozenDefender, frozenAttacker) {
    const events = [];
    if (attackerSkills.megaPowerBonus > 0) {
        events.push({
            type: "megaPower", side: "attacker", attackBonus: attackerSkills.megaPowerBonus,
            sourceRace: attackerSkills.megaPowerSourceRace,
        });
    }
    if (defenderSkills.megaPowerBonus > 0) {
        events.push({
            type: "megaPower", side: "defender", attackBonus: defenderSkills.megaPowerBonus,
            sourceRace: defenderSkills.megaPowerSourceRace,
        });
    }
    if (frozenDefender) {
        events.push({
            type: "freezing", side: "attacker", sourceRace: frozenDefender.sourceRace,
            frozenShipId: frozenDefender.frozen.shipId, frozenRace: frozenDefender.frozen.race,
        });
    }
    if (frozenAttacker) {
        events.push({
            type: "freezing", side: "defender", sourceRace: frozenAttacker.sourceRace,
            frozenShipId: frozenAttacker.frozen.shipId, frozenRace: frozenAttacker.frozen.race,
        });
    }
    return events;
}
// ---------------------------------------------------------------------------
// One full round
// ---------------------------------------------------------------------------
export function snapshotCounts(groups) {
    return new Map(groups.map((g) => [g, g.count]));
}
export function cloneGroups(groups) {
    return groups.map((g) => ({ ...g, perUnit: { ...g.perUnit } }));
}
/**
 * Round-start step for one side, shared by both battle models: recomputes the
 * passive bonuses (`applyRoundStartPassives`) and the Devastate chance from the
 * providers alive now. Returns the side's critical chance for this round. Run
 * BEFORE the round's snapshot is cloned.
 */
export function beginRound(groups, layers, lifePool, baseCritChance) {
    applyRoundStartPassives(groups, layers, lifePool);
    return withLiveDevastate(baseCritChance, groups);
}
function resolveRound(round, attackerGroups, defenderGroups, attackerBaseCritChance, defenderBaseCritChance, rolls, lifePool, layers) {
    const attackerCritChance = beginRound(attackerGroups, layers, lifePool, attackerBaseCritChance);
    const defenderCritChance = beginRound(defenderGroups, layers, lifePool, defenderBaseCritChance);
    const startAttacker = snapshotCounts(attackerGroups);
    const startDefender = snapshotCounts(defenderGroups);
    // Snapshot forces AS THEY WERE at the start of this round for the report.
    const attackerGroupsAtStart = cloneGroups(attackerGroups);
    const defenderGroupsAtStart = cloneGroups(defenderGroups);
    const attackerSkills = resolveStartOfRoundSkills(attackerGroups, rolls);
    const defenderSkills = resolveStartOfRoundSkills(defenderGroups, rolls);
    const frozenDefender = resolveFreezing(attackerGroups, defenderGroups, rolls);
    const frozenAttacker = resolveFreezing(defenderGroups, attackerGroups, rolls);
    const events = buildStartOfRoundEvents(attackerSkills, defenderSkills, frozenDefender, frozenAttacker);
    // Both sides fire using their ROUND-START forces as firepower (the
    // `*GroupsAtStart` clones), never the live, already-mutated groups —
    // "every unit group that was alive at the start of a round still
    // performs its attack during that round, even if destroyed earlier in
    // the round." The live `attackerGroups`/`defenderGroups` still serve as
    // each phase's TARGET bookkeeping (mutated for real, so a firing side's
    // own spillover sees its hits landing in real time, and losses persist
    // into the next round) — only the FIRING side's own count/aliveness is
    // frozen at round-start, so neither side's retaliation is weakened by
    // damage it took earlier in this same round.
    const attackerEntries = fireSide(attackerGroupsAtStart, "attacker", defenderGroups, round, attackerSkills, frozenAttacker?.frozen, attackerCritChance, rolls, lifePool);
    const defenderEntries = fireSide(defenderGroupsAtStart, "defender", attackerGroups, round, defenderSkills, frozenDefender?.frozen, defenderCritChance, rolls, lifePool);
    const attackerLossesThisRound = roundLossCounts(attackerGroups, startAttacker);
    const defenderLossesThisRound = roundLossCounts(defenderGroups, startDefender);
    const revivalEvents = [
        ...resolveRevival(attackerGroups, "attacker", attackerLossesThisRound, rolls, lifePool),
        ...resolveRevival(defenderGroups, "defender", defenderLossesThisRound, rolls, lifePool),
    ];
    return {
        round,
        attackerGroups: attackerGroupsAtStart,
        defenderGroups: defenderGroupsAtStart,
        entries: [...attackerEntries, ...defenderEntries],
        events: [...events, ...revivalEvents],
    };
}
// ---------------------------------------------------------------------------
// Battle points, scrap, demolition — post-battle math
// ---------------------------------------------------------------------------
/** Metal + minerals + gas build cost of `losses` for `race`. */
export function lossesToCost(race, losses) {
    let total = 0;
    for (const [key, count] of Object.entries(losses)) {
        if (!count)
            continue;
        const cost = calculateShipBuildCost(race, Number(key), count);
        total += (cost.metal ?? 0) + (cost.minerals ?? 0) + (cost.gas ?? 0);
    }
    return total;
}
/**
 * `calculateBattlePoints` on already-priced losses (total metal + minerals +
 * gas), for sides that lose ships of more than one race.
 */
export function calculateBattlePointsFromCosts(attackerCost, defenderCost) {
    const attackerResourcePointsLost = Math.floor(attackerCost / PLAYER_POINTS_DIVISOR);
    const defenderResourcePointsLost = Math.floor(defenderCost / PLAYER_POINTS_DIVISOR);
    const totalLost = attackerResourcePointsLost + defenderResourcePointsLost;
    function pointsFor(ownLost, opponentLost) {
        if (opponentLost <= 0)
            return 0;
        if (ownLost <= 0)
            return Math.round(opponentLost * BATTLE_POINTS_NO_LOSS_FACTOR);
        return Math.round(opponentLost * BATTLE_POINTS_FACTOR * (1 + opponentLost / totalLost));
    }
    return {
        attackerResourcePointsLost,
        defenderResourcePointsLost,
        attackerBattlePoints: pointsFor(attackerResourcePointsLost, defenderResourcePointsLost),
        defenderBattlePoints: pointsFor(defenderResourcePointsLost, attackerResourcePointsLost),
    };
}
export function calculateBattlePoints(attackerRace, attackerLosses, defenderRace, defenderLosses) {
    return calculateBattlePointsFromCosts(lossesToCost(attackerRace, attackerLosses), lossesToCost(defenderRace, defenderLosses));
}
/** `calculateBattleScrap` on already-priced losses (both sides' total metal + minerals + gas). */
export function calculateBattleScrapFromCosts(destroyedCost, roll) {
    const clamped = Math.min(1, Math.max(0, roll));
    const step = SCRAP_MULTIPLIER_MIN_STEP + Math.floor(clamped * (SCRAP_MULTIPLIER_MAX_STEP - SCRAP_MULTIPLIER_MIN_STEP + 1));
    const multiplier = Math.min(SCRAP_MULTIPLIER_MAX_STEP, step) * SCRAP_MULTIPLIER_STEP_PERCENT;
    return Math.floor(destroyedCost * multiplier);
}
/** `floor(destroyedCost × randomInt[25..35] × 0.75%)` — the recycle field left behind. */
export function calculateBattleScrap(attackerLosses, attackerRace, defenderLosses, defenderRace, roll) {
    return calculateBattleScrapFromCosts(lossesToCost(attackerRace, attackerLosses) + lossesToCost(defenderRace, defenderLosses), roll);
}
/** Demolition chance for `race`'s id9 ships present at battle's end (`ships.md` "Building Destruction"). Computed only — never applied. */
export function calculateDemolitionChance(race, survivingShips) {
    const count = survivingShips[9] ?? 0;
    if (count <= 0)
        return 0;
    const points = DEMOLITION_POINTS_PER_SHIP[race] * count;
    let chance = 0;
    for (const tier of DEMOLITION_CHANCE_TABLE) {
        if (points >= tier.points)
            chance = tier.chance;
    }
    return chance;
}
// ---------------------------------------------------------------------------
// Top level
// ---------------------------------------------------------------------------
export function totalShipCount(groups) {
    return groups.reduce((sum, g) => sum + g.count, 0);
}
/**
 * Zero-fills every ship type present at battle start (never omits a
 * wiped-out group) — the defending planet's `ships` map is written with a
 * merging transaction (`commitAdvance`'s `tx.set(..., { merge: true })`),
 * which deep-merges nested maps. An omitted key would keep its old value
 * instead of being cleared, resurrecting a destroyed ship type. Harmless on
 * the attacker's fleet doc, which is written `merge: false`.
 */
export function groupsToPlanetShips(groups) {
    const result = {};
    for (const g of groups) {
        result[g.shipId] = g.count;
    }
    return result;
}
export function computeLosses(startGroups, endGroups) {
    const losses = {};
    for (const start of startGroups) {
        const end = endGroups.find((g) => g.shipId === start.shipId);
        const lost = start.count - (end?.count ?? 0);
        if (lost > 0)
            losses[start.shipId] = lost;
    }
    return losses;
}
/** `critChance` with its Devastate part recomputed from the providers alive in `groups`. */
export function withLiveDevastate(critChance, groups) {
    const providers = liveProviders(groups, "devastate");
    return {
        base: critChance.base,
        devastateBonus: summedStrength(providers, (race) => DEVASTATE_CHANCE_PER_SHIP[race], DEVASTATE_MAX_CHANCE),
        devastateSourceShipIds: providerShipIds(providers),
    };
}
export function criticalChanceFor(side, groups) {
    const scienceBonus = getScienceBonus(side.sciences);
    const base = scienceBonus.surpriseTactics.criticalChance + (side.admiralBonuses?.criticalChanceBonus ?? 0);
    return withLiveDevastate({ base, devastateBonus: 0, devastateSourceShipIds: [] }, groups);
}
/** Effective critical chance for `shipId`'s own attacks this round — the Devastate bonus never applies to its providers' ship ids. */
export function critChanceForShip(critChance, shipId) {
    const bonus = critChance.devastateSourceShipIds.includes(shipId) ? 0 : critChance.devastateBonus;
    return Math.min(1, critChance.base + bonus);
}
/**
 * Resolve a full battle. Loops rounds until `input.rounds` is reached or one
 * side has zero total ships, per "combat continues round by round until one
 * side has no remaining units, or the attacker's chosen rounds are done."
 */
export function resolveBattle(input, rolls) {
    const rollCursor = new RollCursor(rolls);
    const attackerStart = buildUnitGroups(input.attacker);
    const defenderStart = buildUnitGroups(input.defender);
    const attackerGroups = cloneGroups(attackerStart);
    const defenderGroups = cloneGroups(defenderStart);
    const attackerCritChance = criticalChanceFor(input.attacker, attackerGroups);
    const defenderCritChance = criticalChanceFor(input.defender, defenderGroups);
    const lifePool = initLifePool([...attackerGroups, ...defenderGroups]);
    const layers = initPassiveLayers([...attackerGroups, ...defenderGroups]);
    const rounds = [];
    for (let round = 1; round <= input.rounds; round++) {
        if (totalShipCount(attackerGroups) === 0 || totalShipCount(defenderGroups) === 0)
            break;
        rounds.push(resolveRound(round, attackerGroups, defenderGroups, attackerCritChance, defenderCritChance, rollCursor, lifePool, layers));
    }
    removeBonusLifeLayers([...attackerGroups, ...defenderGroups], layers, lifePool);
    const attackerSurvivors = groupsToPlanetShips(attackerGroups);
    const defenderSurvivors = groupsToPlanetShips(defenderGroups);
    const attackerLosses = computeLosses(attackerStart, attackerGroups);
    const defenderLosses = computeLosses(defenderStart, defenderGroups);
    const points = calculateBattlePoints(input.attacker.race, attackerLosses, input.defender.race, defenderLosses);
    const scrap = calculateBattleScrap(attackerLosses, input.attacker.race, defenderLosses, input.defender.race, rollCursor.next());
    const attackerTotal = totalShipCount(attackerGroups);
    const defenderTotal = totalShipCount(defenderGroups);
    const outcome = attackerTotal > 0 && defenderTotal === 0 ? "attackerWins" :
        defenderTotal > 0 && attackerTotal === 0 ? "defenderWins" :
            "draw";
    return {
        rounds,
        attackerSurvivors,
        defenderSurvivors,
        attackerLosses,
        defenderLosses,
        points,
        scrap,
        demolitionPoints: {
            attackerChance: calculateDemolitionChance(input.attacker.race, attackerSurvivors),
            defenderChance: calculateDemolitionChance(input.defender.race, defenderSurvivors),
        },
        outcome,
        passivesPerRound: true,
    };
}
//# sourceMappingURL=battle.js.map